import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { App as AntApp, Button } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import { useInstallPrompt } from '../context/InstallPromptContext';
import { useAuth } from '../context/AuthContext';
import { useMemoGateOpen } from '../lib/memoGateStore';

/**
 * InstallPrompt — the bottom sheet that walks an agent through putting the app on
 * their phone's home screen.
 *
 * There is no single install flow to document: Chromium hands us a native dialog,
 * iOS only has the Safari share menu, and an agent who tapped the link inside
 * WhatsApp is in a webview that can't install at all. So the sheet resolves the
 * agent's actual situation to ONE set of instructions rather than listing every
 * platform and making them find themselves in it.
 *
 * Surfaces ~1.5s after load (not installed, not dismissed in the last 30 days) so
 * the page paints first — from the login screen onward, so an agent can install
 * before signing in — and is reopenable any time from the More tab.
 */
export default function InstallPrompt() {
  const {
    showPrompt,
    autoShow,
    manualOpen,
    platform,
    browser,
    canInstallNatively,
    promptInstall,
    dismiss,
    closePrompt,
  } = useInstallPrompt();
  const { message } = AntApp.useApp();
  const { isAuthed } = useAuth() || {};
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  // Public customer signing is not an agent — never nudge it to install the agent
  // app. The login screen DOES get the nudge: on iOS a home-screen app runs in its
  // own storage container, so an agent who signs in inside Safari and installs
  // afterwards lands on a logged-out app and has to sign in a second time. Asking
  // before the session exists means they install first and log in once, in the app.
  // Reset-password stays quiet — mid-recovery is the wrong moment to interrupt.
  // A memo from HQ the agent must confirm comes first; the sheet waits for it and
  // comes back once it is confirmed.
  const memoOpen = useMemoGateOpen();
  const suppressed =
    pathname.startsWith('/sign/') || pathname.startsWith('/join/') || !(isAuthed || pathname === '/login') || memoOpen;

  // Manual opens are immediate; the automatic nudge waits a beat so the screen
  // behind it has rendered its data first.
  useEffect(() => {
    if (!showPrompt || suppressed) {
      setVisible(false);
      return undefined;
    }
    if (manualOpen) {
      setVisible(true);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [showPrompt, manualOpen, autoShow, suppressed]);

  const { mounted, closing } = useSheetAnim(visible);

  const handleInstall = async () => {
    const { outcome } = await promptInstall();
    if (outcome === 'accepted') {
      setVisible(false);
      closePrompt();
    }
  };

  // Closing a manual open just closes — the agent opened it on purpose, so don't
  // re-arm the 30-day dismissal. Closing the auto nudge records it so we stop asking.
  const handleClose = () => {
    setVisible(false);
    if (manualOpen) closePrompt();
    else dismiss();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      message.success('Link copied — paste it into your browser');
    } catch {
      message.info(window.location.origin);
    }
  };

  if (!mounted) return null;

  const guide = buildGuide({ platform, browser, canInstallNatively });

  return (
    <div
      onClick={handleClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ width: '100%', maxWidth: 460, margin: '0 auto', background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '88%', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '10px 16px 0', flex: 'none' }}>
          <div style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto' }} />
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <img
              src="/icon-192.png"
              alt=""
              style={{ width: 52, height: 52, borderRadius: 13, border: '1px solid var(--border)', flex: 'none' }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15.5, fontWeight: 600 }}>{guide.title}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{guide.subtitle}</div>
            </div>
          </div>

          {guide.intro && (
            <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, marginBottom: guide.steps.length ? 14 : 4 }}>
              {guide.intro}
            </div>
          )}

          {guide.note && (
            <div style={{ display: 'flex', gap: 10, background: 'color-mix(in srgb,var(--warn) 12%,transparent)', border: '1px solid color-mix(in srgb,var(--warn) 30%,transparent)', borderRadius: 11, padding: '14px 15px', marginBottom: 14 }}>
              <span style={{ color: 'var(--warn)', fontWeight: 700, fontSize: 13, lineHeight: 1.45, flex: 'none' }}>!</span>
              <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.45 }}>{guide.note}</div>
            </div>
          )}

          {guide.steps.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 6 }}>
              {guide.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ flex: 'none', width: 21, height: 21, borderRadius: 50, background: 'var(--primary)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    {i + 1}
                  </span>
                  <div style={{ fontSize: 13, lineHeight: 1.5, flex: 1 }}>{s}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '15px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          {canInstallNatively ? (
            <>
              <Button block onClick={handleClose}>Not now</Button>
              <Button block type="primary" onClick={handleInstall}>Install</Button>
            </>
          ) : browser.inApp ? (
            <>
              <Button block onClick={handleClose}>Close</Button>
              <Button block type="primary" onClick={copyLink}>Copy link</Button>
            </>
          ) : (
            <Button block type="primary" onClick={handleClose}>Got it</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Inline glyphs so a step can name the button the agent is looking for rather than
// describing it ("the share button") and hoping they match it to the right icon.
const Glyph = ({ children, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', margin: '0 2px', color: 'var(--primary)' }}>
    {children}
  </svg>
);
const ShareIOS = () => <Glyph><path d="M12 3 v12" /><path d="M8.5 6.5 12 3l3.5 3.5" /><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" /></Glyph>;
const PlusSquare = () => <Glyph><rect x="3.5" y="3.5" width="17" height="17" rx="4" /><path d="M12 8v8M8 12h8" /></Glyph>;
const Kebab = () => <Glyph><circle cx="12" cy="5" r="1.3" fill="currentColor" /><circle cx="12" cy="12" r="1.3" fill="currentColor" /><circle cx="12" cy="19" r="1.3" fill="currentColor" /></Glyph>;

const b = (t) => <strong style={{ fontWeight: 600 }}>{t}</strong>;

/**
 * Resolves platform + browser + whether Chromium handed us a native prompt into the
 * one flow that actually works for this agent. Ordered most-blocking first: an
 * in-app webview has no install route at all, so it takes precedence over the OS.
 */
function buildGuide({ platform, browser, canInstallNatively }) {
  const appName = browser.name ? `the ${browser.name} browser` : 'this app';

  if (browser.inApp) {
    return {
      title: 'Open in your browser first',
      subtitle: "Apps can't be installed from inside a chat app",
      intro: `You opened this link inside ${appName}. Reopen it in ${platform === 'ios' ? 'Safari' : 'Chrome'} and the install option becomes available.`,
      steps: platform === 'ios'
        ? [
            <>Tap <ShareIOS /> or {b('⋯')} at the bottom of this screen</>,
            <>Choose {b('Open in Safari')}</>,
            <>In Safari, go to {b('More → Install app')} and follow the steps</>,
          ]
        : [
            <>Tap <Kebab /> at the top right of this screen</>,
            <>Choose {b('Open in Chrome')} (or {b('Open in browser')})</>,
            <>In Chrome, go to {b('More → Install app')} and follow the steps</>,
          ],
    };
  }

  if (platform === 'ios') {
    return {
      title: 'Install CIDAS',
      subtitle: 'Add it to your iPhone home screen',
      intro: 'Opens full screen with no address bar, and launches straight from your home screen like any other app.',
      note: browser.isSafari
        ? null
        : `Add to Home Screen is most reliable in Safari — if you don't see it in ${appName}, open this page in Safari instead.`,
      steps: [
        <>Tap the <ShareIOS /> {b('Share')} button {browser.isSafari ? 'at the bottom of Safari' : 'in the browser toolbar'}</>,
        <>Scroll down and tap <PlusSquare /> {b('Add to Home Screen')}</>,
        <>Tap {b('Add')} at the top right to confirm</>,
      ],
    };
  }

  if (platform === 'android') {
    if (canInstallNatively) {
      return {
        title: 'Install CIDAS',
        subtitle: 'Add it to your home screen',
        intro: 'Tap Install and confirm. The app opens full screen with no address bar, and launches straight from your home screen.',
        steps: [],
      };
    }
    return {
      title: 'Install CIDAS',
      subtitle: 'Add it to your Android home screen',
      intro: 'Opens full screen with no address bar, and launches straight from your home screen like any other app.',
      steps: [
        <>Tap <Kebab /> {b('Menu')} at the top right of {appName}</>,
        <>Tap {b('Install app')} (or {b('Add to Home screen')})</>,
        <>Confirm with {b('Install')}</>,
      ],
    };
  }

  if (canInstallNatively) {
    return {
      title: 'Install CIDAS',
      subtitle: 'Add it to this computer',
      intro: 'Tap Install to run CIDAS in its own window, without browser tabs and the address bar.',
      steps: [],
    };
  }
  return {
    title: 'Install CIDAS',
    subtitle: 'Add it to this computer',
    intro: 'Runs in its own window, without browser tabs and the address bar.',
    note: 'Installing needs Chrome, Edge or another Chromium browser. On your phone you also get it on the home screen.',
    steps: [
      <>Look for the {b('install')} icon at the right of the address bar</>,
      <>Or open <Kebab /> {b('Menu')} → {b('Cast, save and share')} → {b('Install page as app')}</>,
      <>Confirm with {b('Install')}</>,
    ],
  };
}
